/**
 * VolumesPage Component
 *
 * Main page for managing volume resources with search functionality.
 * Follows the same approach as GroupsPage for UI consistency.
 */

import React, { useState, useMemo } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { VolumeTable } from '../components/VolumeTable';
import { CreateVolumeModal } from '../components/CreateVolumeModal';
import { UpdateVolumeModal } from '../components/UpdateVolumeModal';
import { VolumeDetailsModal } from '../components/VolumeDetailsModal';
import { DeleteVolumeModal } from '../components/DeleteVolumeModal';
import { useVolumes } from '../hooks/useVolumes';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/utils/permissions';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { VolumeSanitized } from '../../../types/volume.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * VolumesPage component
 */
export const VolumesPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedVolume, setSelectedVolume] = useState<VolumeSanitized | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const tenantId = tenant?.tenantId || '';

  // Permission checks
  const canCreate = hasPermission(activeRole?.permissions, 'resources:volume:create');
  const canUpdate = hasPermission(activeRole?.permissions, 'resources:volume:update');
  const canDelete = hasPermission(activeRole?.permissions, 'resources:volume:delete');

  // Debounced search (300ms delay)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch volume resources
  const {
    data: volumes = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useVolumes(tenantId, {
    search: debouncedSearch || undefined,
  });

  // Filter volumes locally (additional filtering if needed)
  const filteredVolumes = useMemo(() => {
    if (!volumes) return [];

    let filtered = [...volumes];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.volumeName.toLowerCase().includes(query) ||
          item.volumeCode.toLowerCase().includes(query) ||
          (item.description && item.description.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [volumes, debouncedSearch]);

  const handleViewDetails = (volume: VolumeSanitized) => {
    setSelectedVolume(volume);
    setShowDetailsModal(true);
  };

  const handleEdit = (volume: VolumeSanitized) => {
    setSelectedVolume(volume);
    setShowUpdateModal(true);
  };

  const handleDelete = (volume: VolumeSanitized) => {
    setSelectedVolume(volume);
    setShowDeleteModal(true);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Volumes</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Manage object storage volumes and application access
            </p>
          </div>
          {canCreate && (
            <Button onClick={() => setShowCreateModal(true)}>
              Create Volume
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search volumes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Volume Table */}
        <VolumeTable
          volumes={filteredVolumes}
          isLoading={isLoading}
          onView={handleViewDetails}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canDelete={canDelete}
        />

        {/* Modals */}
        <CreateVolumeModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {showDetailsModal && selectedVolume && (
          <VolumeDetailsModal
            isOpen={showDetailsModal}
            onClose={() => {
              setShowDetailsModal(false);
              setSelectedVolume(null);
            }}
            tenantId={tenantId}
            volumeId={selectedVolume.volumeId}
          />
        )}

        {showUpdateModal && selectedVolume && (
          <UpdateVolumeModal
            isOpen={showUpdateModal}
            onClose={() => {
              setShowUpdateModal(false);
              setSelectedVolume(null);
            }}
            tenantId={tenantId}
            volume={selectedVolume}
          />
        )}

        {showDeleteModal && selectedVolume && (
          <DeleteVolumeModal
            isOpen={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedVolume(null);
            }}
            volume={selectedVolume}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
